import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../context/Translator";
import axios from "axios";
import PageLayout from "../../../layouts/PageLayout";
import { Row, Col, Form, Button, Pagination, Modal, Table } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import { ButtonComponent } from "../../../components/elements";
import { FiEdit, FiTrash2, FiSearch, FiX, FiShield, FiPlus, FiRefreshCw } from "react-icons/fi";
import Swal from 'sweetalert2';

export default function RoleIndex() {
    const { t } = useContext(TranslatorContext);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pagination, setPagination] = useState({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
        from: 0,
        to: 0
    });

    const navigate = useNavigate();
    const [filters, setFilters] = useState({
        name: "",
        per_page: 10
    });

    const permissions = JSON.parse(localStorage.getItem("permissions")) || [];

    const hasPermission = (perm) => {
        return permissions.includes(perm);
    };


    const [searchName, setSearchName] = useState("");
    const [applyingFilter, setApplyingFilter] = useState(false);
    const [deleteModal, setDeleteModal] = useState({ show: false, roleId: null, roleName: "" });

    // Fetch roles
    const fetchRoles = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("authToken");
            const params = new URLSearchParams();
            if (filters.name) params.append('name', filters.name);
            if (filters.per_page) params.append('per_page', filters.per_page);

            const url = `${process.env.REACT_APP_API_URL}/roles${params.toString() ? `?${params.toString()}` : ''}`;

            const res = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (res.data.success) {
                const rolesData = res.data.roles;
                setRoles(rolesData.data);
                setPagination({
                    current_page: rolesData.current_page || 1,
                    last_page: rolesData.last_page || 1,
                    per_page: rolesData.per_page || 10,
                    total: rolesData.total || 0,
                    from: rolesData.from || 0,
                    to: rolesData.to || 0
                });
            }
        } catch (err) {
            console.error("Error fetching roles:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Failed to load roles"),
                showConfirmButton: false,
                timer: 2000
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchRoles();
    }, [filters]);

    // Handle search
    const handleSearch = () => {
        setFilters(prev => ({ ...prev, name: searchName }));
    };

    const handleReset = () => {
        setSearchName("");
        setFilters({ name: "", per_page: 10 });
    };

    // Handle per page change
    const handlePerPageChange = (e) => {
        setFilters(prev => ({ ...prev, per_page: parseInt(e.target.value) }));
    };

    // Handle page change
    const handlePageChange = (page) => {
        if (page >= 1 && page <= pagination.last_page) {
            setFilters(prev => ({ ...prev, page }));
        }
    };

    // Handle delete
    const handleDelete = async () => {
        try {
            const token = localStorage.getItem("authToken");
            await axios.delete(`${process.env.REACT_APP_API_URL}/roles/${deleteModal.roleId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'success',
                title: t("Role deleted successfully"),
                showConfirmButton: false,
                timer: 2000
            });

            setDeleteModal({ show: false, roleId: null, roleName: "" });
            fetchRoles();
        } catch (err) {
            console.error("Error deleting role:", err);
            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: t("Failed to delete role"),
                showConfirmButton: false,
                timer: 2000
            });
        }
    };

    const confirmDelete = (roleId, roleName) => {
        setDeleteModal({ show: true, roleId, roleName });
    };

    if (loading && roles.length === 0) {
        return (
            <PageLayout>
                <div className="text-center p-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">{t("Loading...")}</span>
                    </div>
                    <p className="mt-3">{t("Loading roles...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">{t('Roles Management')}</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">{t('home')}</Link>
                                </li>
                                <li className="mc-breadcrumb-item">{t('Roles')}</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    <div className="mc-card">
                        <div className="mc-card-header p-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
                            <h5 className="mc-card-title mb-0">{t('Role List')}</h5>
                            <div className="d-flex gap-2">
                                {hasPermission("User roles create") && (

                                    <ButtonComponent
                                        type="button"
                                        className="btn btn-primary"
                                        onClick={() => navigate("/role-create")}
                                        text={
                                            <>
                                                <FiPlus className="me-1" /> {t("Create New Role")}
                                            </>
                                        }
                                    />
                                )}
                            </div>
                        </div>

                        {/* Filter Section */}
                        <div className="p-3 border-bottom" style={{ backgroundColor: '#f8f9fa' }}>
                            <Row className="g-3 align-items-end">
                                <Col md={4}>
                                    <Form.Group>
                                        <Form.Label>{t("Role Name")}</Form.Label>
                                        <Form.Control
                                            type="text"
                                            value={searchName}
                                            onChange={(e) => setSearchName(e.target.value)}
                                            placeholder={t("Search by role name")}
                                            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <div className="d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            onClick={handleSearch}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiSearch /> {t("Search")}
                                        </Button>
                                        <Button
                                            variant="secondary"
                                            onClick={handleReset}
                                            className="d-flex align-items-center gap-1"
                                        >
                                            <FiRefreshCw /> {t("Reset")}
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </div>

                        {/* Results Info */}
                        <div className="p-3 border-bottom d-flex justify-content-between align-items-center">
                            <small className="text-muted">
                                {t("Showing")} {pagination.from} {t("to")} {pagination.to} {t("of")} {pagination.total} {t("entries")}
                            </small>
                            {loading && (
                                <small>
                                    <div className="spinner-border spinner-border-sm text-primary me-1" role="status" />
                                    {t("Refreshing...")}
                                </small>
                            )}
                        </div>

                        <div className="mc-card">
                            <div className="table-responsive-wrapper">
                                <Table responsive className="table mb-0">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>{t('Role Name')}</th>
                                            <th>{t('Permissions')}</th>
                                            <th>{t('Created At')}</th>
                                            <th width="150">{t('Action')}</th>
                                        </tr>
                                    </thead>
                                    <tbody >
                                        {roles.length > 0 ? (
                                            roles.map((role, index) => (
                                                <tr key={role.id}>
                                                    <td>{(pagination.current_page - 1) * pagination.per_page + index + 1}</td>
                                                    <td>
                                                        <strong>
                                                            <FiShield className="me-2" />
                                                            {role.name}
                                                        </strong>
                                                    </td>
                                                    <td>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                gap: "6px",
                                                                flexWrap: "wrap",
                                                                maxWidth: "400px",
                                                                maxHeight: "100px",
                                                                overflowY: "auto",
                                                                padding: "4px"
                                                            }}
                                                        >
                                                            {role.permissions && role.permissions.length > 0 ? (
                                                                role.permissions.map((permission, i) => (
                                                                    <span
                                                                        key={i}
                                                                        style={{
                                                                            backgroundColor: "#e7f3ff",
                                                                            padding: "4px 8px",
                                                                            borderRadius: "6px",
                                                                            fontSize: "11px",
                                                                            border: "1px solid #d1e4f5",
                                                                            whiteSpace: "nowrap"
                                                                        }}
                                                                    >
                                                                        {permission.name}
                                                                    </span>
                                                                ))
                                                            ) : (
                                                                <span className="text-muted">{t("No permissions")}</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{new Date(role.created_at).toLocaleDateString()}</td>
                                                    <td>
                                                        <div className="d-flex gap-2">
                                                            {hasPermission("User roles edit") && (
                                                                <ButtonComponent
                                                                    type="button"
                                                                    className="btn btn-sm btn-primary"
                                                                    onClick={() => navigate(`/role-edit/${role.id}`)}
                                                                    title={t("Edit")}
                                                                >
                                                                    <FiEdit />
                                                                </ButtonComponent>
                                                            )}
                                                            {/* <ButtonComponent
                                                            type="button"
                                                            className="btn btn-sm btn-danger"
                                                            onClick={() => confirmDelete(role.id, role.name)}
                                                            title={t("Delete")}
                                                        >
                                                            <FiTrash2 />
                                                        </ButtonComponent> */}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="5" className="text-center py-5">
                                                    <div className="text-muted">
                                                        <FiShield size={48} className="mb-3" style={{ opacity: 0.5 }} />
                                                        <p>{t("No roles found")}</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        </div>

                        {/* Pagination */}
                        {pagination.last_page > 1 && (
                            <div className="p-3 border-top">
                                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                    <div className="text-muted small">
                                        {t("Page")} {pagination.current_page} {t("of")} {pagination.last_page}
                                    </div>
                                    <Pagination className="mb-0">
                                        <Pagination.First
                                            onClick={() => handlePageChange(1)}
                                            disabled={pagination.current_page === 1}
                                        />
                                        <Pagination.Prev
                                            onClick={() => handlePageChange(pagination.current_page - 1)}
                                            disabled={pagination.current_page === 1}
                                        />

                                        {[...Array(Math.min(5, pagination.last_page))].map((_, i) => {
                                            let pageNum;
                                            if (pagination.last_page <= 5) {
                                                pageNum = i + 1;
                                            } else if (pagination.current_page <= 3) {
                                                pageNum = i + 1;
                                            } else if (pagination.current_page >= pagination.last_page - 2) {
                                                pageNum = pagination.last_page - 4 + i;
                                            } else {
                                                pageNum = pagination.current_page - 2 + i;
                                            }

                                            if (pageNum >= 1 && pageNum <= pagination.last_page) {
                                                return (
                                                    <Pagination.Item
                                                        key={pageNum}
                                                        active={pageNum === pagination.current_page}
                                                        onClick={() => handlePageChange(pageNum)}
                                                    >
                                                        {pageNum}
                                                    </Pagination.Item>
                                                );
                                            }
                                            return null;
                                        })}

                                        <Pagination.Next
                                            onClick={() => handlePageChange(pagination.current_page + 1)}
                                            disabled={pagination.current_page === pagination.last_page}
                                        />
                                        <Pagination.Last
                                            onClick={() => handlePageChange(pagination.last_page)}
                                            disabled={pagination.current_page === pagination.last_page}
                                        />
                                    </Pagination>
                                </div>
                            </div>
                        )}
                    </div>
                </Col>
            </Row>

            {/* Delete Confirmation Modal */}
            <Modal show={deleteModal.show} onHide={() => setDeleteModal({ show: false, roleId: null, roleName: "" })} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t("Delete Role")}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t("Are you sure you want to delete the role")} <strong>{deleteModal.roleName}</strong>?</p>
                    <p className="text-danger small">{t("This action cannot be undone.")}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setDeleteModal({ show: false, roleId: null, roleName: "" })}>
                        {t("Cancel")}
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        {t("Delete")}
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}